<template>
  <p class="body-3-bold tool-desc text-center text-base pb-4">
    {{ $t("calculator-suite.fibonacciDesc") }}
  </p>
  <div class="calculator-params-results">
    <section class="params-card">
      <h2 class="body-3-bold">{{ $t("calculator-suite.setParams") }}</h2>
      <div class="flex flex-column">
        <div v-for="input in fibonacciParams" :key="input.key">
          <InputNumber
            v-if="!input.options"
            v-model="input.value"
            :label="$t(input.text)"
            :placeholder="$t(input.placeholder)"
            :name="input.text"
            :tooltip-message="$t(input.tooltip)"
            tooltip-dir-desk="right"
            tooltip-dir-mobile="top"
            :tooltip-max-width="246"
          />
        </div>
        <ErrorMessage v-if="errorMessage" :msg="errorMessage" />
      </div>

      <button
        class="primary-button-medium"
        @click="calculate"
        :disabled="!isFormValid || isLoading"
      >
        {{ $t("calculator-suite.calculate") }}
      </button>
    </section>

    <section class="results-card w-full flex flex-column">
      <ResultsSkeleton v-if="isLoading" class="p-3" />

      <div class="p-4 md:p-0" v-else>
        <h2 class="body-3-bold">{{ $t("calculator-suite.results") }}:</h2>
        <div class="result-info mb-4 fibonacci pt-2 pb-4 mt-3">
          <h4 class="body-4 flex gap-2 align-items-center mb-0 pl-3 text-base">
            {{ $t("calculator-suite.retracements") }}:
            <Tooltip
              :max-width-tooltip="246"
              float-tooltip-mobile="right"
              float-tooltip-desk="right"
              use-material-icon="info"
              style="cursor: pointer"
            >
              <template #title>
                <p v-html="$t('calculator-suite.retracementsTooltip')" />
              </template>
            </Tooltip>
          </h4>
          <div
            v-for="result in retracements"
            :key="result.percentage"
            class="item px-3"
            style="background-color: #ecf1f7"
          >
            <span class="body-4">
              {{ formatNumber(result.percentage, 1) }}%
            </span>
            <span class="body-4-bold">
              {{ formatNumber(result.value, 2) }}
            </span>
          </div>
        </div>

        <div class="result-info mb-4 fibonacci pt-2 pb-4 mt-3">
          <h4 class="body-4 flex gap-2 align-items-center mb-0 pl-3 text-base">
            {{ $t("calculator-suite.extensions") }}:
            <Tooltip
              :max-width-tooltip="246"
              float-tooltip-mobile="right"
              float-tooltip-desk="right"
              use-material-icon="info"
              style="cursor: pointer"
            >
              <template #title>
                <p v-html="$t('calculator-suite.extensionsTooltip')" />
              </template>
            </Tooltip>
          </h4>
          <div
            v-for="result in extensions"
            :key="result.percentage"
            class="item px-3"
            style="background-color: #ecf1f7"
          >
            <span class="body-4">
              {{ formatNumber(result.percentage, 1) }}%
            </span>
            <span class="body-4-bold">
              {{ formatNumber(result.value, 2) }}
            </span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import InputNumber from "../InputNumber.vue";
import Tooltip from "../Tooltip.vue";
import ResultsSkeleton from "./ResultsSkeleton.vue";
import ErrorMessage from "./ErrorMessage.vue";
import { fibonacciCalculate } from "./utils/calculateRequest";

const { t } = useI18n();
const emit = defineEmits(["isLoading"]);

const fibonacciParams = ref([
  {
    text: "calculator-suite.highSwing",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.highSwingTooltip",
    key: "high",
    value: null,
  },
  {
    text: "calculator-suite.customVal",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.customValTooltip",
    key: "custom",
    value: null,
  },
  {
    text: "calculator-suite.lowSwing",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.lowSwingTooltip",
    key: "low",
    value: null,
  },
]);

const retracements = ref([]);
const extensions = ref([]);
const isLoading = ref(false);
const errorMessage = ref(null);

const isFormValid = computed(() => {
  const highValid =
    fibonacciParams.value.find((p) => p.key === "high").value !== null;
  const lowValid =
    fibonacciParams.value.find((p) => p.key === "low").value !== null;

  return highValid && lowValid;
});

const calculate = async () => {
  isLoading.value = true;
  errorMessage.value = null;

  const paramsData = fibonacciParams.value.reduce((acc, { key, value }) => {
    if (value !== null && value !== "") {
      acc[key] = Number(value);
    }
    return acc;
  }, {});

  try {
    const data = await fibonacciCalculate(paramsData);

    retracements.value = data.retracements;
    extensions.value = data.extensions;
  } catch (error) {
    console.error(error);

    if (error.message.includes("'high' must be greater than 'low'")) {
      errorMessage.value = t("calculator-suite.priceBetween");
    } else {
      errorMessage.value = t("calculator-suite.priceHigher");
    }
    return;
  } finally {
    isLoading.value = false;
  }
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || isNaN(value)) {
    return "";
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};
</script>
